import { ICommonComponentEditorProps } from 'components/ComponentType';
import {
    IDatasetSetpointConfig,
    ITableTitles,
} from 'components/dashboard/dataset-setpoint-table/common/api/interfaces/interfaces';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import SetpointTableTitlesEditor from "./setpoint-table-titles-editor/SetpointTableTitlesEditor";

const DatasetSetpointValuesEditor = lazy(
    () => import("./dataset-setpoint-values-editor/DatasetSetpointValuesEditor")
);

const defaultDatasetSetpointConfig: IDatasetSetpointConfig = {
    showTagColumn: true,
    showSetpointColumn: true,
    updateFrequency: 5,
    showTableTitles: true,
    tableTitles: {
        datasetTitle: '',
        datasetValueTitle: '',
        setpointValueTitle: '',
        tagTitle: '',
    },
    values: [],
    compactMode: false
};

const DatasetSetpointTableEditor = ({ config, onConfigChange }: ICommonComponentEditorProps) => {
    let conf: IDatasetSetpointConfig = config ?? defaultDatasetSetpointConfig;

    if (Object.keys(conf).length === 0) {
        conf = defaultDatasetSetpointConfig;
    }

    const [currentConfig, setCurrentConfig] = useState<IDatasetSetpointConfig>(conf);
    const [isValuesEditorVisible, setValuesEditorVisibility] = useState<boolean>(false);

    const setConfigPropertyState = useCallback(
        (property: keyof IDatasetSetpointConfig, value: any) => {
            setCurrentConfig((prevState) => ({ ...prevState, [property]: value }));
        },
        [currentConfig]
    );

    const handleConfigChange = useCallback(
        (id: string, value: any): void => {
            setConfigPropertyState(id as keyof IDatasetSetpointConfig, value);
        },
        [setConfigPropertyState]
    );

    const handleTableTitlesChange = useCallback(
        (id: string, value: any): void => {
            const newConfig = structuredClone(currentConfig);
            if (newConfig) {
                newConfig.tableTitles![id as keyof ITableTitles] = value;
            }
            setCurrentConfig(newConfig);
        },
        [currentConfig]
    );

    useEffect(() => {
        onConfigChange(currentConfig);
    }, [currentConfig]);

    return (
        <div className='grid p-fluid'>
            <div className='col-2 gap-2 flex flex-column justify-content-center align-items-center'>
                <label htmlFor='updateFrequency'>Update frequency</label>
                <div className='p-inputgroup w-full'>
                    <InputNumber
                        id='updateFrequency'
                        value={currentConfig.updateFrequency as number}
                        onValueChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    />
                    <span className='p-inputgroup-addon'>s</span>
                </div>
            </div>
            <div className='col-2 gap-2 flex flex-column justify-content-center align-items-center'>
                <label htmlFor='showTagColumn'>Tag column</label>
                <InputSwitch
                    id='showTagColumn'
                    type='checkbox'
                    checked={currentConfig.showTagColumn}
                    onChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    tooltip={currentConfig.showTagColumn ? 'Tag Column visible' : 'Tag Column hidden'}
                />
            </div>
            <div className='col-2 gap-2 flex flex-column justify-content-center align-items-center'>
                <label htmlFor='showSetpointColumn'>Setpoint column</label>
                <InputSwitch
                    id='showSetpointColumn'
                    type='checkbox'
                    checked={currentConfig.showSetpointColumn}
                    onChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    tooltip={currentConfig.showSetpointColumn ? 'Setpoint Column visible' : 'Setpoint Column hidden'}
                />
            </div>
            <div className='col-2 gap-2 flex flex-column justify-content-center align-items-center'>
                <label htmlFor='compactMode'>Compact mode</label>
                <InputSwitch
                    id='compactMode'
                    type='checkbox'
                    checked={currentConfig.compactMode}
                    onChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    tooltip={currentConfig.compactMode ? 'Compact mode enabled' : 'Compact mode disabled'}
                />
            </div>
            <div className='col-2 gap-2 flex flex-column justify-content-center align-items-center'>
                <label htmlFor='showTableTitles'>Table Titles</label>
                <InputSwitch
                    id='showTableTitles'
                    type='checkbox'
                    checked={currentConfig.showTableTitles}
                    onChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    tooltip={currentConfig.showTableTitles ? 'Table titles visible' : 'Table titles hidden'}
                />
            </div>
            <div className='col-2 gap-2 flex flex-column justify-content-center align-items-center'>
                <Button
                    icon='pi pi-list'
                    className='p-button mt-3'
                    type='button'
                    tooltip='Values'
                    onClick={() => setValuesEditorVisibility(true)}
                />
            </div>

            <Dialog
                header='Values'
                visible={isValuesEditorVisible}
                style={{ width: '85vw' }}
                onHide={() => setValuesEditorVisibility(false)}
                maximizable
            >
                <Suspense fallback={<ProgressSpinner />}>
                    <DatasetSetpointValuesEditor currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
                </Suspense>
            </Dialog>
            {currentConfig.showTableTitles && (
                <SetpointTableTitlesEditor
                    currentConfig={currentConfig}
                    handleTableTitlesChange={handleTableTitlesChange}
                />
            )}
        </div>
    );
};

export default DatasetSetpointTableEditor;
